/**
 * Plays the synthesised cues (`cues.ts`) through the game's audio bus.
 *
 * Owns: the cue-level limits. Must not: create an `AudioContext` or know the
 * bus: `GameAudio` registers its context and the effects gain node here
 * (`setSfxPort`), so master volume, mute, the compressor and the `sfxVolume`
 * setting all apply, and this file has no import of `index.ts`. With no port
 * registered (the landing, tests) or a context the browser has not yet let
 * run, `playCue` does nothing: a blocked context would queue the cue and
 * burst it out on the first tap. Motion settings are not consulted: reduced
 * motion is not reduced sound.
 */
import { CUE_BUILDERS, MAX_CUE_S, type CueId } from './cues';

export interface SfxPort {
  ctx: AudioContext;
  /** Where cues connect: the effects gain, which feeds the master bus. */
  out: AudioNode;
}

/** Two of the same cue inside this window are one event (eight coins landing at once). */
export const DEBOUNCE_S = 0.04;
/** Cues sounding at once, across all ids; a ninth would only mud the rest. */
export const MAX_VOICES = 6;

let port: SfxPort | null = null;
const lastAt = new Map<CueId, number>();
/** Audio-clock times at which the voices now sounding end. */
let ends: number[] = [];

export function setSfxPort(next: SfxPort | null): void {
  port = next;
  lastAt.clear();
  ends = [];
}

/** Returns whether the cue was started. `gain` multiplies the cue's own level. */
export function playCue(id: CueId, opts: { gain?: number } = {}): boolean {
  if (!port || port.ctx.state !== 'running') return false;
  const { ctx, out } = port;
  const now = ctx.currentTime;
  ends = ends.filter((end) => end > now);
  const last = lastAt.get(id);
  if ((last !== undefined && now - last < DEBOUNCE_S) || ends.length >= MAX_VOICES) return false;
  lastAt.set(id, now);
  ends.push(now + MAX_CUE_S);
  const voice = ctx.createGain();
  voice.gain.value = opts.gain ?? 1;
  voice.connect(out);
  CUE_BUILDERS[id](ctx, voice, now);
  return true;
}
